/*!
 * Copyright 2019 Fondazione Bruno Kessler
 * Licensed under the EUPL, Version 1.1 or - as soon they will be approved by the European
 * Commission - subsequent versions of the EUPL (the "Licence"); You may not use this work
 * except in compliance with the Licence.
 *
 * You may obtain a copy of the Licence at:
 * http://ec.europa.eu/idabc/eupl.html
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the Licence is distributed on an "AS IS" basis, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the Licence for the specific language
 * governing permissions and limitations under the Licence.
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  FormGroupDirective,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from "@angular/forms";
import {ConfigSectionComponent} from "@app/models/components/config-section.component";
import {
  CONTEXTS_INFO,
  ParentRegistryContexts,
  RegistryContextInfo,
  RegistryContexts,
  UserEntryKey
} from "@app/models/constants/registry-constants";
import {AppSelectionExt} from "@app/models/selection";
import {CommonActions} from "@app/store/common/common.actions";
import {CommonState} from "@app/store/common/common.state";
import {PatientTreatmentPlanMonState} from "@modules/doctor-panel/modules/patient-treatment-plan/store/patient-treatment-plan-mon.state";
import {hasActionsExecuting} from "@ngxs-labs/actions-executing";
import {Select, Store} from "@ngxs/store";
import {Appointment, SaluteProxyService} from "@trec/api";
import {DATE_FMT_8601, RangeDates, TrecTranslation, UNLIMITED_DATE} from "@trec/api/common";
import {ReasonerWarningPrescription} from "@trec/api/reasoner";
import {
  UeTelemonitoringData,
  UserEntry2,
  UserEntryTelemonitoringDataCause,
} from "@trec/api/registry";
import {C3DictionaryActions, C3DictionaryState} from "@trec/app-modules/dictionary-shared";
import {StoreAction} from "@trec/components/store";
import {format, parseISO} from "date-fns";
import {Observable, Subscription, of, take} from "rxjs";
import {TpMonActions} from "../../../store/patient-treatment-plan-mon.actions";
import {teleRowValidator} from "./validators/telerow.validator";

export const enum TM_DATES_TYPES {
  END_OF_PRESCRIPTION = "end_of_prescription",
  SPECIFIC_DATE = "specific_date",
}

export interface TeleItem {
  contextId: string;
  telemonitoring: boolean;
  start_date: Date | null;
  end_date: Date | null;
  tmDatesType?: TM_DATES_TYPES;
  extraordinaryData?: {cause: UserEntryTelemonitoringDataCause; reasonText?: string};
}

/* eslint-disable @typescript-eslint/no-non-null-assertion */

@Component({
  selector: "c3-config-telemonitoring",
  templateUrl: "./config-telemonitoring.component.html",
  styleUrls: ["./config-telemonitoring.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ConfigTelemonitoringComponent
  extends ConfigSectionComponent
  implements OnInit, OnDestroy {
  // Data /////////////////////////////////////////////////////////////////////////////////////////

  @Select(CommonState.patientTelemonitoringDates)
  telemonitoringDates$: Observable<UeTelemonitoringData | null>;
  @Select(CommonState.patientUserEntryByContext)
  patientUserEntryByContext$: Observable<UserEntry2[] | null>;

  @Select(PatientTreatmentPlanMonState.isChatEnabled) isChatEnabled$: Observable<
    boolean | undefined
  >;
  @Select(
    hasActionsExecuting([
      TpMonActions.FetchWarningsOS,
      TpMonActions.FetchTelemonitoringConfig,
      TpMonActions.UpdateWarningsOS,
      TpMonActions.UpdateTelemonitoringConfig,
      TpMonActions.UpdateChatConfigStatus,
      TpMonActions.FetchMotiviInterruzioneTelemonitoraggio,
      TpMonActions.UpdateTelemonitoringConfigV2,
      CommonActions.FetchAllPatientContexts
    ])
  )
  loading$: Observable<boolean>;

  @Select(PatientTreatmentPlanMonState.warningsOSPrescriptions)
  prescriptions$: Observable<ReasonerWarningPrescription[] | null>;

  @Select(C3DictionaryState.availableWarnings)
  availableWarnings$: Observable<TrecTranslation[] | undefined>;

  @Select(CommonState.patientContext)
  context$: Observable<string[] | null | undefined>;

  @Select(PatientTreatmentPlanMonState.motiviInterruzioneTelemonitoraggio)
  motiviInterruzione$: Observable<string[]>;

  @ViewChild("tmForm", {read: FormGroupDirective, static: true})
  formDirective: FormGroupDirective;

  @Input()
  context: RegistryContexts[] = [];

  contextInfo: {[context: string]: RegistryContextInfo} = {};

  reasonList?: string[];

  parentContext: string | null = null;
  patientContexts: string[] = [];

  consensusDeniedFromApp = false;
  consensusText = "";

  initialised = false;

  readonly CTXS_PARENT = ParentRegistryContexts;

  // Others ///////////////////////////////////////////////////////////////////////////////////////
  // Se vuoi tenere solo quelli che hanno un contextId valido:
  // .filter(e => !!e.contextId);

  readonly today = new Date();

  // Provide this values to the template //////////////////////////////////////////////////////////
  readonly TM_END_OF_PRESCRIPTION = TM_DATES_TYPES.END_OF_PRESCRIPTION;
  readonly TM_SPECIFIC_DATE = TM_DATES_TYPES.SPECIFIC_DATE;

  get appPrescrEndDate(): Date {
    const appPrescr = this.store.selectSnapshot(CommonState.appPrescriptionDates);
    return appPrescr ? new Date(appPrescr.end) : new Date(UNLIMITED_DATE);
  }

  /**
   * Max tm date is 28 days or the end of the app prescr if it is < 28 days
   */
  // get maxTelemonDate(): Date | undefined {
  //   const appPrescrEndDate = this.appPrescrEndDate;
  //   // tm max end date = tm start date + 28days
  //   const tmMaxEndDate = moment(this.tmStartControl.value || this.today).add(28, 'days');
  //   return tmMaxEndDate.isAfter(appPrescrEndDate) ? appPrescrEndDate : tmMaxEndDate.toDate();
  // }

  // Form /////////////////////////////////////////////////////////////////////////////////////////
  /** comodo getter */
  get teleArray(): FormArray {
    return this.form.get("teleArray") as FormArray;
  }

  /** boolean aggregato: true se almeno un item è attivo */
  get isTelemonitoringEnabled(): boolean {
    const fa = this.teleArray;
    if (!fa || fa.length === 0) return false;
    return fa.controls.some((g) => g.get("telemonitoring")?.value === true);
  }

  get tmDatesTypeControl(): AbstractControl {
    return this.form.get("tmDatesType")!;
  }
  get tmStartControl(): AbstractControl {
    return this.form.get("tmStart")!;
  }
  get tmEndControl(): AbstractControl {
    return this.form.get("tmEnd")!;
  }
  get telemonEndReasonControl(): AbstractControl {
    return this.form.get("telemonEndReason")!;
  }

  form: UntypedFormGroup;

  get touched$(): Observable<boolean> {
    return of(true);
  }

  get valid$(): Observable<boolean> {
    return this.extractValidFromForm(this.form);
  }

  get tmStart(): Date | null {
    return this.form.get("tmStart")!.value;
  }

  get tmEnd(): Date | null {
    return this.form.get("tmEnd")!.value;
  }

  get tmRangeDates(): RangeDates | null {
    const start = this.tmStart;
    const end = this.tmEnd;
    return start && end ? {start, end} : null;
  }

  get telemonEndReason(): string | null {
    return this.form.get("telemonEndReason")!.value;
  }

  private subscr = new Subscription();

  constructor(
    private store: Store,
    private fb: UntypedFormBuilder,
    private cd: ChangeDetectorRef,
    private saluteProxyService: SaluteProxyService
  ) {
    super();

    this.form = this.fb.group({
      // isTelemonitoringEnabled: new UntypedFormControl(undefined, [Validators.required]),
      // tmDatesType: new UntypedFormControl(null, [Validators.required]),
      // tmStart: new UntypedFormControl(null, [Validators.required, Validators.min(this.today.valueOf())]),
      // tmEnd: new UntypedFormControl(null, [Validators.required, Validators.min(this.today.valueOf())]),
      warningsSelection: new FormControl(null, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      isChatEnabled: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      telemonEndReason: new FormControl<string>("", []),
      teleArray: this.fb.array([], []),
    });
  }

  get shouldShowWarningSelections() {
    const availableCtx = ['diabetes1', 'diabetes2', 'gestational', 'pneumo', 'parki', 'oncology', 'cdcd', 'perioperative', 'stomachcare', 'woundcare'];
    return !this.patientContexts.some((item) => availableCtx.includes(item))
  }

  getInvalidControls(form: FormGroup): string[] {
    return Object.keys(form.controls)
      .filter(key => form.controls[key]);
  }

  init() {
    this.subscr.add(
      this.patientUserEntryByContext$.subscribe((userEntry) => {
        const ueTelemonitoring = userEntry?.find((x) => x.telemonitoring);

        this.initFromUserEntry(userEntry);

        if (ueTelemonitoring?.telemonitoring) {
          let telemonitoring = ueTelemonitoring?.telemonitoring;
          const today = new Date().valueOf();
          const telemEnabled =
            new Date(telemonitoring.start_date).valueOf() <= today &&
            new Date(telemonitoring.end_date).valueOf() > today;

          this.form.patchValue({
            isTelemonitoringEnabled: telemEnabled,
            tmDatesType:
              new Date(telemonitoring.end_date).valueOf() === this.appPrescrEndDate.valueOf()
                ? TM_DATES_TYPES.END_OF_PRESCRIPTION
                : TM_DATES_TYPES.SPECIFIC_DATE,
            tmStart: new Date(telemonitoring.start_date),
            // If tm end is old -> set today as new default end date
            tmEnd:
              new Date(telemonitoring.end_date) > this.today
                ? new Date(telemonitoring.end_date)
                : today,
          });
        } else {
          //se attiva telemonitoraggio è disattivato vado a recuperare il motivo
          const telemonitoringEndReason = userEntry
            ?.find((x) => !x.telemonitoring)
            ?.getKey(UserEntryKey.TELEMONITORING_END_REASON);

          this.form.patchValue({
            isTelemonitoringEnabled: false,
            tmDatesType: null,
            tmStart: null,
            tmEnd: null,
            telemonEndReason: telemonitoringEndReason,
          });
        }
        this.cd.markForCheck();
        this.form.updateValueAndValidity();
      })
    );


    // Get consensus
    const userEntry = this.store.selectSnapshot(CommonState.patientUserEntry);
    this.saluteProxyService.getConsensus(userEntry![0].patientId).subscribe((resp) => {
      if (resp["INFORMATIVA APP"] === false) {
        this.consensusDeniedFromApp = true;
        this.consensusText = "Il paziente non ha accettato l'informativa";
      }
      if (resp["CONDIZIONI USOAPP"] === false) {
        this.consensusDeniedFromApp = true;
        if (this.consensusText.length > 0) {
          this.consensusText = this.consensusText + " - ";
        }
        this.consensusText =
          this.consensusText + " Il paziente non ha accettato le condizioni d'uso ";
      }
    });

    // Get warnings
    this.subscr.add(
      this.prescriptions$.subscribe((prescriptions) => {
        const control = this.form.get('warningsSelection');
        if (prescriptions && this.shouldShowWarningSelections) {
          // Set as selected all warnings that have a prescription
          const selection = prescriptions
            .filter((x) => x.hasOwnProperty("id"))
            .reduce(
              (a, p) => ({...a, [p.warningId]: {checked: true, priority: p.priority}}),
              {}
            );
          this.form.patchValue({warningsSelection: selection});
          this.cd.markForCheck();
        } else {
          control?.clearValidators();
          control?.disable();
        }
        this.form.updateValueAndValidity({emitEvent: false});
      })
    );

    // Get chat enabled status
    this.subscr.add(
      this.isChatEnabled$.subscribe((isChatEnabled) => {
        this.form.patchValue({isChatEnabled: !!isChatEnabled});
        this.cd.markForCheck();
      })
    );

    //lista dei motivi di interruzione gravidanza
    this.subscr.add(
      this.motiviInterruzione$.subscribe((reasonList) => {
        this.reasonList = reasonList;
        this.cd.markForCheck();
      })
    );
    if (!this.initialised) {
      if (this.reasonList === null) {
        //popolamento della combo dei motivi
        this.store.dispatch(new TpMonActions.FetchMotiviInterruzioneTelemonitoraggio());
      }

      // Fetch available list and required data
      // this.context$.subscribe((val) => {
      //   this.patientContexts = val;
      // });
      this.store.dispatch(new C3DictionaryActions.FetchAvailableWarnings({}));
      this.store.dispatch(new TpMonActions.FetchTelemonitoringConfig());
      this.store.dispatch(new TpMonActions.FetchWarningsOS());


      this.initialised = true;
    }
  }

  ngOnInit() {
    // this.initForm();
    this.context$.pipe(take(1)).subscribe((context) => {
      this.contextInfo = CONTEXTS_INFO;
      if (Object.keys(this.contextInfo).length === 0) {
        const contextInfoLocalStorage = localStorage.getItem("CONTEXTS_INFO");
        ; this.contextInfo =
          contextInfoLocalStorage != null ? JSON.parse(contextInfoLocalStorage) : undefined;
      }
      if (context) {
        this.patientContexts = context;
      }
      this.init()
    });
  }

  datePreservingUtc(value: string): Date {
    const source = new Date(value);

    return new Date(
      source.getUTCFullYear(),
      source.getUTCMonth(),
      source.getUTCDate(),
      source.getUTCHours(),
      source.getUTCMinutes(),
      source.getUTCSeconds(),
      source.getUTCMilliseconds()
    );
  }

  /** inizializza l’array dal tuo userEntry */
  initFromUserEntry(userEntry: UserEntry2[] | null): void {
    const items: TeleItem[] = (userEntry || [])
      .map((item: UserEntry2) => {
        const tm = item.telemonitoring
        const start = tm?.start_date ? this.datePreservingUtc(tm?.start_date.toISOString()) : null
        const end = tm?.end_date ? this.datePreservingUtc(tm?.end_date.toISOString()) : null
        return {
          contextId: item.contextId || "",
          telemonitoring: !!tm,
          start_date: start,
          end_date: end,
          tmDatesType: tm?.end_date
            ? new Date(tm?.end_date).getTime() !== this.appPrescrEndDate.getTime()
              ? TM_DATES_TYPES.SPECIFIC_DATE
              : TM_DATES_TYPES.END_OF_PRESCRIPTION
            : TM_DATES_TYPES.END_OF_PRESCRIPTION,
        }

      })

    const fa = this.teleArray;
    fa.clear();
    items
      .filter(item => !!this.contextInfo[item.contextId])
      .forEach((it) => fa.push(this.buildTeleGroup(it)));

    // 🔑 ricalcola la validità dell’intero form
    fa.updateValueAndValidity({emitEvent: false});
    this.form.updateValueAndValidity({emitEvent: false});
  }

  /** costruisce un FormGroup per riga */
  private buildTeleGroup(it: TeleItem): FormGroup {
    const fg = this.fb.group(
      {
        contextId: [it.contextId],
        telemonitoring: [it.telemonitoring], // meglio null all'inizio se vuoi forzare la scelta
        start_date: [it.start_date],
        end_date: [it.end_date],
        tmDatesType: [it.tmDatesType],
      },
      {validators: [teleRowValidator]}
    );

    // Assicura il ricalcolo del validator di riga quando cambiano i campi
    fg.get("start_date")!.valueChanges.subscribe(() =>
      fg.updateValueAndValidity({emitEvent: false})
    );
    fg.get("end_date")!.valueChanges.subscribe(() =>
      fg.updateValueAndValidity({emitEvent: false})
    );
    fg.get("telemonitoring")!.valueChanges.subscribe(() =>
      fg.updateValueAndValidity({emitEvent: false})
    );
    fg.get("tmDatesType")!.valueChanges.subscribe(() =>
      fg.updateValueAndValidity({emitEvent: false})
    );

    return fg;
  }

  onToggleTele(i: number, value: boolean): void {
    const fg = this.teleArray.at(i) as FormGroup;

    if (value) {
      fg.patchValue(
        {
          telemonitoring: true,
          start_date: new Date(),
          end_date: this.appPrescrEndDate || null,
        },
        {emitEvent: true}
      );
    } else {
      fg.patchValue(
        {
          telemonitoring: false,
          start_date: null,
          end_date: null,
        },
        {emitEvent: true}
      );
    }

    fg.updateValueAndValidity({emitEvent: true});
  }

  // onDatesTypeChange(i: number, type: "end_of_prescription" | "specific_date"): void {
  //   const fg = this.teleArray.at(i) as FormGroup;
  //   fg.patchValue({tmDatesType: type}, {emitEvent: false});

  //   if (type === "end_of_prescription") {
  //     fg.patchValue({end_date: this.appPrescrEndDate || null}, {emitEvent: false});
  //   } else {
  //     // lascio la scelta all’utente via datepicker
  //   }

  //   fg.updateValueAndValidity({emitEvent: false});
  // }

  // temporaneo, per evitare lo scostamento delle date
  // TODO gestire le date come data pure, senza orario lato be e fe
  private parseDate(date: Date, eod?: boolean) {
    return new Date(
      Date.UTC(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        eod ? 23 : 0,
        eod ? 59 : 0,
        eod ? 59 : 0,
        eod ? 999 : 0
      )
    );
  }


  /** payload finale */
  buildPayload(): TeleItem[] {
    return (this.teleArray.value as TeleItem[]).map((v) => ({
      contextId: v.contextId,
      telemonitoring: !!v.telemonitoring,
      start_date: v.start_date ? this.parseDate(v.start_date) : null,
      end_date: v.end_date ? this.parseDate(v.end_date, true) : null,
      tmDatesType: v.tmDatesType || TM_DATES_TYPES.END_OF_PRESCRIPTION,
    }));
  }

  prepareSaveDataAction(): StoreAction | StoreAction[] | null {
    if (this.form.invalid) {
      this.form.updateValueAndValidity();
      // Trigger Angular validation
      this.formDirective.onSubmit(null as any);

      throw new Error(
        `Cannot prepare save data for telemonitoring,
         form is invalid:
         | tmDatesTy: ${JSON.stringify(this.tmDatesTypeControl.errors)}
         | tmStart: ${JSON.stringify(this.tmStartControl.errors)}
         | tmEnd: ${JSON.stringify(this.tmEndControl.errors)}
         | value: ${JSON.stringify(this.form.value)}`
      );
    }

    if (this.isTelemonitoringEnabled) {
      // Telemon enabled
      // const tmStart = this.form.get('tmStart')!.value;
      // const tmStart = this.today;
      // let tmEnd = this.form.get("tmEnd")!.value as Date;

      // if (this.form.get("tmDatesType")!.value === TM_DATES_TYPES.END_OF_PRESCRIPTION) {
      //   tmEnd = this.appPrescrEndDate;
      // }

      const isChatEnabled = this.form.get("isChatEnabled")!.value;
      const wSelection: AppSelectionExt | null = this.form.get("warningsSelection")!.value;

      const payload = this.buildPayload()

      const {startDateOld, endDateOld} = (() => {
        let minStart;
        let maxEnd;

        for (const t of payload) {
          if (!t?.start_date || !t?.end_date) continue;

          const s = t.start_date;
          const e = t.end_date;
          if (Number.isNaN(+s) || Number.isNaN(+e)) continue; // ignora date non valide

          if (!minStart || s < minStart) minStart = s;
          if (!maxEnd || e > maxEnd) maxEnd = e;
        }

        return {
          startDateOld: minStart ? minStart.toISOString() : undefined,
          endDateOld: maxEnd ? maxEnd.toISOString() : undefined,
        };
      })();

      if (!startDateOld || !endDateOld) {
        throw new Error(
          `Cannot prepare save data for telemonitoring, missing dates [${startDateOld}-${endDateOld}]`
        );
      }

      const structureId = this.store.selectSnapshot(CommonState.currentStructureId);
      const userEntry = this.store.selectSnapshot(CommonState.patientUserEntry);
      const currentPrescriptions =
        this.store.selectSnapshot(PatientTreatmentPlanMonState.warningsOSPrescriptions) || [];
      const availableWarnings = this.store.selectSnapshot(C3DictionaryState.availableWarnings);

      if (!userEntry) {
        throw new Error(
          `Try to update warnings config but userEntry of current patient id is missing`
        );
      }
      if (!structureId) {
        throw new Error(`Try to update warnings config but current structureId is missing`);
      }
      if (!availableWarnings) {
        throw new Error(`Try to update warnings config but availableWarnings are unknown`);
      }

      const patientId = userEntry[0].patientId;

      // Insert only warnings that are selected but have no prescription  -> prescription needs to be created
      const insert: ReasonerWarningPrescription[] = Object.entries(wSelection ?? {})
        .filter((entry) => entry[1].checked) // Only selected
        .filter(
          (selectedW) =>
            !currentPrescriptions.find((prescr) => prescr.warningId === selectedW[0]) ||
            currentPrescriptions.find(
              (prescr) =>
                prescr.warningId === selectedW[0] && selectedW[1].priority !== prescr.priority
            )
        )
        .map((w) => ({
          // Create warning prescription
          trecUserId: patientId,
          warningId: w[0],
          deliveryType: "EMAIL",
          targetId: structureId,
          from: format(new Date(), DATE_FMT_8601), // From now
          to: format(parseISO(endDateOld), DATE_FMT_8601), // Until telemoitoring end
          warningType: "os",
          priority: w[1].priority,
          id: currentPrescriptions.find((prescr) => prescr.warningId === w[0])?.id,
        }));

      // Remove only warnings which are prescribed but not selected -> warnings to unprescribe
      const remove: ReasonerWarningPrescription[] = currentPrescriptions
        .filter(
          (prescr) =>
            wSelection?.[prescr.warningId] !== undefined && !wSelection?.[prescr.warningId].checked
        )
        .filter((prescr) => prescr.id !== undefined);

      // Send actions
      // return [
      //   new TpMonActions.UpdateTelemonitoringConfig({
      //     telemonConfig: {dates: {start: tmStart, end: tmEnd}},
      //     chatEnabled: isChatEnabled,
      //     insertWarnings: insert,
      //     removeWarnings: remove,
      //     telemonEndReason: null,
      //   }),
      //   //new TpMonActions.UpdateWarningsOS({insert, remove}),
      // ];
      // Send actions
      return [
        new TpMonActions.UpdateTelemonitoringConfigV2({
          telemonConfig: payload,
          chatEnabled: isChatEnabled,
          insertWarnings: insert,
          removeWarnings: remove,
          telemonEndReason: null,
        }),
        //new TpMonActions.UpdateWarningsOS({insert, remove}),
      ];
    } else {
      // Telemon disabled

      // telemon disabled -> disable tm, warnings, chat

      // Get all warnings to disable
      const wRemove =
        this.store.selectSnapshot(PatientTreatmentPlanMonState.warningsOSPrescriptions) || [];
      return [
        new TpMonActions.UpdateTelemonitoringConfig({
          telemonConfig: {dates: null},
          chatEnabled: false,
          insertWarnings: null,
          removeWarnings: null,
          telemonEndReason: this.telemonEndReason,
        }),
        new TpMonActions.UpdateWarningsOS({insert: [], remove: wRemove}),
      ];
    }
  }

  resetTouched(): void {
    this.form.markAsUntouched();
  }

  ngOnDestroy() {
    if (this.subscr) {
      this.subscr.unsubscribe();
    }
  }

  getAppointments(): Observable<Appointment[]> {
    const userEntry = this.store.selectSnapshot(CommonState.patientUserEntry);
    return this.saluteProxyService.getAppointments(
      userEntry![0].patientId,
      localStorage.getItem("c3-curr-context")!.toUpperCase()
    );
  }

  onSaveComplete() {
    const patientId = this.store.selectSnapshot(CommonState.patientId);
    const structure = this.store.selectSnapshot(CommonState.currentStructureId);
    const userId = this.store.selectSnapshot(CommonState.userId);
    if (patientId && structure && userId) {
      this.store.dispatch(new CommonActions.FetchAllPatientContexts({
        patientId,
        structureId: structure
      }))

      this.store.dispatch(
        new CommonActions.SavePatientTelemonitoring({
          context: localStorage.getItem("c3-curr-context")!.toUpperCase(),
          patientId: patientId,
          structure: structure,
          userId: userId,
        })
      );
    }
  }
}
